"use client";

import { Badge, Box, Card, Flex, Text } from "@radix-ui/themes";

interface ScoreBreakdown {
  [key: string]: number;
}

interface RosterCreator {
  creatorName?: string;
  username: string;
  platform?: string;
  followers?: number | null;
  averageViews?: number | null;
  engagement?: number | null;
  price?: number;
  postFormat?: string | null;
  themes?: string[];
  vibes?: string[];
  momentumPct?: number;
  score: number;
  scoreBreakdown?: ScoreBreakdown;
  reasons?: string[];
  source?: "roster";
}

interface DiscoveryCreator {
  username: string;
  displayName?: string | null;
  platform?: string;
  followers?: number | null;
  totalViews?: number;
  totalLikes?: number;
  engagementRatePct?: number;
  alreadyInRoster?: boolean;
  postUrl?: string | null;
  score: number;
  scoreBreakdown?: ScoreBreakdown;
  reasons?: string[];
}

export interface CreatorToolResult {
  error?: string;
  message?: string;
  creators?: (RosterCreator | DiscoveryCreator)[];
  returned?: number;
  totalFound?: number;
  totalCandidates?: number;
}

export interface CreatorResultsProps {
  toolName: "recommend_creators" | "discover_creators";
  result: CreatorToolResult;
}

const ROSTER_BREAKDOWN_LABELS: Record<string, string> = {
  performance: "Performance",
  fit: "Fit",
  momentum: "Momentum",
  budget: "Budget",
};

const DISCOVERY_BREAKDOWN_LABELS: Record<string, string> = {
  engagement: "Engagement",
  reach: "Reach",
  novelty: "New",
  multiMatch: "Multi-match",
};

function formatCount(n?: number | null): string {
  if (n == null || !Number.isFinite(n)) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function scoreTierColor(score: number): string {
  if (score >= 75) return "var(--green-9)";
  if (score >= 50) return "var(--blue-9)";
  if (score >= 25) return "var(--amber-9)";
  return "var(--gray-8)";
}

function ScoreBar({ score }: { score: number }) {
  const pct = Math.min(Math.max(score, 0), 100);
  return (
    <Box className="creator-score-bar" title={`${pct}/100`}>
      <Box
        className="creator-score-bar__fill"
        style={{
          width: `${pct}%`,
          background: scoreTierColor(pct),
        }}
      />
    </Box>
  );
}

function BreakdownSegments({
  breakdown,
  labels,
}: {
  breakdown: ScoreBreakdown;
  labels: Record<string, string>;
}) {
  const entries = Object.entries(breakdown).filter(([, v]) => v > 0);
  if (entries.length === 0) return null;

  return (
    <Flex gap="1" wrap="wrap" className="creator-breakdown" mt="1">
      {entries.map(([key, value]) => (
        <Flex
          key={key}
          align="center"
          gap="1"
          className="creator-breakdown__segment"
        >
          <Text size="1" color="gray">
            {labels[key] ?? key}
          </Text>
          <Badge size="1" variant="soft" color="gray">
            {value}
          </Badge>
        </Flex>
      ))}
    </Flex>
  );
}

function isDiscoveryCreator(
  c: RosterCreator | DiscoveryCreator,
): c is DiscoveryCreator {
  return "alreadyInRoster" in c || "totalViews" in c;
}

function CreatorCard({
  creator,
  toolName,
}: {
  creator: RosterCreator | DiscoveryCreator;
  toolName: CreatorResultsProps["toolName"];
}) {
  const discovery = isDiscoveryCreator(creator);
  const breakdown = creator.scoreBreakdown ?? {};
  const labels =
    toolName === "discover_creators"
      ? DISCOVERY_BREAKDOWN_LABELS
      : ROSTER_BREAKDOWN_LABELS;

  const displayName = discovery
    ? creator.displayName
    : (creator as RosterCreator).creatorName;

  const profileUrl = discovery
    ? creator.postUrl
    : undefined;

  return (
    <Card variant="surface" className="creator-result-card">
      <Flex justify="between" align="start" gap="2" wrap="wrap">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Flex align="center" gap="2" wrap="wrap">
            {profileUrl ? (
              <a
                href={profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="creator-result-card__username"
              >
                @{creator.username}
              </a>
            ) : (
              <Text size="2" weight="bold" className="creator-result-card__username">
                @{creator.username}
              </Text>
            )}
            {displayName && displayName !== creator.username && (
              <Text size="1" color="gray">
                {displayName}
              </Text>
            )}
            {discovery && (
              <Badge
                size="1"
                color={creator.alreadyInRoster ? "gray" : "green"}
                variant="soft"
              >
                {creator.alreadyInRoster ? "In roster" : "New"}
              </Badge>
            )}
          </Flex>

          <Flex gap="1" wrap="wrap" mt="2" className="creator-result-card__chips">
            {creator.followers != null && (
              <Badge size="1" variant="outline" color="gray">
                {formatCount(creator.followers)} followers
              </Badge>
            )}
            {discovery ? (
              <>
                {creator.totalViews != null && creator.totalViews > 0 && (
                  <Badge size="1" variant="outline" color="gray">
                    {formatCount(creator.totalViews)} views
                  </Badge>
                )}
                {creator.engagementRatePct != null && creator.engagementRatePct > 0 && (
                  <Badge size="1" variant="outline" color="gray">
                    {creator.engagementRatePct}% like rate
                  </Badge>
                )}
              </>
            ) : (
              <>
                {(creator as RosterCreator).averageViews != null && (
                  <Badge size="1" variant="outline" color="gray">
                    ~{formatCount((creator as RosterCreator).averageViews)} avg views
                  </Badge>
                )}
                {(creator as RosterCreator).price != null && (
                  <Badge size="1" variant="outline" color="gray">
                    ${(creator as RosterCreator).price}
                  </Badge>
                )}
                {(creator as RosterCreator).themes?.map((t) => (
                  <Badge key={`t-${t}`} size="1" variant="soft" color="blue">
                    {t}
                  </Badge>
                ))}
                {(creator as RosterCreator).vibes?.map((v) => (
                  <Badge key={`v-${v}`} size="1" variant="soft" color="purple">
                    {v}
                  </Badge>
                ))}
              </>
            )}
          </Flex>
        </Box>

        <Flex direction="column" align="end" gap="1" style={{ flexShrink: 0 }}>
          <Text
            size="3"
            weight="bold"
            style={{ color: scoreTierColor(creator.score) }}
          >
            {creator.score}/100
          </Text>
          <Box style={{ width: 72 }}>
            <ScoreBar score={creator.score} />
          </Box>
        </Flex>
      </Flex>

      <BreakdownSegments breakdown={breakdown} labels={labels} />
    </Card>
  );
}

export default function CreatorResults({ toolName, result }: CreatorResultsProps) {
  if (result.error || result.message) {
    const text = result.message ?? result.error ?? "Discovery unavailable";
    return (
      <Card variant="surface" className="creator-results-notice" mt="2">
        <Text size="2" color="amber">
          {text}
        </Text>
      </Card>
    );
  }

  const creators = result.creators ?? [];
  if (creators.length === 0) return null;

  const title =
    toolName === "discover_creators"
      ? "Discovered creators"
      : "Roster recommendations";

  const count =
    result.returned ?? result.totalFound ?? result.totalCandidates ?? creators.length;

  return (
    <Box className="creator-results" mt="3">
      <Flex justify="between" align="center" mb="2">
        <Text size="1" weight="bold" color="gray" style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {title}
        </Text>
        <Text size="1" color="gray">
          {count} shown
        </Text>
      </Flex>
      <Flex direction="column" gap="2">
        {creators.map((creator, i) => (
          <CreatorCard
            key={`${creator.username}-${i}`}
            creator={creator}
            toolName={toolName}
          />
        ))}
      </Flex>
    </Box>
  );
}
